/**
 * PasskeySection — iter174l (Iter 1 · Grundgerüst)
 * Passwortloser Login-Faktor. User kann Passkeys registrieren + verwalten.
 *
 * Iter 1: nur „Passkey hinzufügen" + Liste + Löschen.
 * Iter 2 (Ondato-Phase): Login-Seite mit „Mit Passkey anmelden"-Button.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Fingerprint, Loader2, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import InfoCard from "@/components/toggles/InfoCard";

/* WebAuthn-Base64URL-Helpers */
const b64uToBuf = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
  return buf.buffer;
};
const bufToB64u = (buf) =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

function fmtDate(iso) {
  if (!iso) return "—";
  return String(iso).slice(0, 19).replace("T", " ");
}

export default function PasskeySection() {
  const { t } = useTranslation();
  const [supported, setSupported] = useState(true);
  const [creds, setCreds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/passkey/credentials");
      setCreds(data.credentials || []);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    setSupported(typeof window !== "undefined"
                  && !!window.PublicKeyCredential);
    load();
  }, [load]);

  const addPasskey = async () => {
    setErr(""); setOk(""); setBusy(true);
    try {
      const { data: opts } = await api.post("/passkey/register/options");
      // Convert Base64URL fields to ArrayBuffer
      const publicKey = {
        ...opts,
        challenge: b64uToBuf(opts.challenge),
        user: { ...opts.user, id: b64uToBuf(opts.user.id) },
        excludeCredentials: (opts.excludeCredentials || []).map((c) => ({
          ...c, id: b64uToBuf(c.id),
        })),
      };
      const cred = await navigator.credentials.create({ publicKey });
      if (!cred) throw new Error("Passkey-Erstellung abgebrochen.");
      const payload = {
        id: cred.id,
        rawId: bufToB64u(cred.rawId),
        type: cred.type,
        response: {
          clientDataJSON: bufToB64u(cred.response.clientDataJSON),
          attestationObject: bufToB64u(cred.response.attestationObject),
          transports: cred.response.getTransports?.() || [],
        },
        clientExtensionResults: cred.getClientExtensionResults?.() || {},
        authenticatorAttachment: cred.authenticatorAttachment,
        passkey_name: window.prompt(
          'Namen für diesen Passkey (z. B. "iPhone", "YubiKey"):',
          "Neuer Passkey",
        ) || "Neuer Passkey",
      };
      await api.post("/passkey/register/verify", payload);
      setOk("Passkey wurde registriert.");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
                || e?.message || "Passkey-Registrierung fehlgeschlagen.";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const removePasskey = async (id) => {
    if (!window.confirm(t("passkey.delete_confirm"))) return;
    setErr(""); setOk("");
    try {
      await api.delete(`/passkey/credentials/${id}`);
      setOk("Passkey gelöscht.");
      load();
    } catch (e) {
      if (e?.response?.data?.detail?.code === "LAST_LOGIN_FACTOR") {
        setErr("Das ist dein letzter Login-Weg. Aktiviere erst 2FA oder ein Passwort, bevor du diesen Passkey löschst.");
      } else {
        setErr(e?.response?.data?.detail?.message || "Löschen fehlgeschlagen.");
      }
    }
  };

  return (
    <div className="px-4 py-4" data-testid="passkey-section">
      <p className="text-sm font-medium text-[#0A0A0A] flex items-center gap-1.5">
        <Fingerprint size={14} /> Passkeys (passwortloser Login)
      </p>
      <p className="mt-1 text-[11px] text-[#666] max-w-lg">
        Ein Passkey ersetzt Passwörter durch biometrisch bestätigte Schlüssel (Face ID, Touch ID, Windows Hello, YubiKey).
        <b className="text-emerald-700"> Beta:</b> Registrierung ist verfügbar, der Login-per-Passkey folgt in einer nächsten Iteration.
      </p>

      {!supported && (
        <InfoCard variant="warning" size="sm" className="mt-3" icon={<AlertTriangle size={12} />} testId="passkey-unsupported">
          Dieser Browser unterstützt keine Passkeys. Nutze einen aktuellen Chrome, Safari, Firefox oder Edge.
        </InfoCard>
      )}

      <button
        data-testid="passkey-add-btn"
        onClick={addPasskey}
        disabled={!supported || busy}
        className="mt-3 h-10 px-4 text-xs font-semibold bg-[#0A0A0A] text-white hover:bg-[#222] disabled:opacity-50 flex items-center gap-1.5"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Fingerprint size={12} />}
        Passkey hinzufügen
      </button>

      {err && (
        <InfoCard variant="danger" size="sm" className="mt-2" icon={<AlertTriangle size={12} />} testId="passkey-error">
          {err}
        </InfoCard>
      )}
      {ok && (
        <InfoCard variant="success" size="sm" className="mt-2" icon={<CheckCircle2 size={12} />} testId="passkey-success">
          {ok}
        </InfoCard>
      )}

      {creds.length > 0 && (
        <div className="mt-3 border border-[#E5E5E5]" data-testid="passkey-list">
          <div className="px-3 py-2 bg-[#F5F5F5] text-[11px] font-semibold text-[#666]">
            Registrierte Passkeys ({creds.length})
          </div>
          <ul className="divide-y divide-[#E5E5E5]">
            {creds.map((c) => (
              <li key={c.credential_id}
                  data-testid={`passkey-row-${c.credential_id.slice(0, 8)}`}
                  className="px-3 py-2 flex items-center justify-between gap-2">
                <div className="text-xs">
                  <p className="font-medium text-[#0A0A0A] flex items-center gap-1.5">
                    <Fingerprint size={12} /> {c.name || "Passkey"}
                    {c.counter_warning && (
                      <span className="text-[10px] text-red-700 bg-red-50 px-1"
                            title="Counter-Regression erkannt — evtl. kompromittiert">
                        ⚠ Warnung
                      </span>
                    )}
                  </p>
                  <p className="text-[10px] text-[#999]">
                    erstellt {fmtDate(c.created_at)}
                    {c.last_used_at && ` · zuletzt verwendet ${fmtDate(c.last_used_at)}`}
                    {c.transports?.length > 0 && ` · ${c.transports.join(", ")}`}
                  </p>
                </div>
                <button
                  data-testid={`passkey-delete-${c.credential_id.slice(0, 8)}`}
                  onClick={() => removePasskey(c.credential_id)}
                  className="p-1.5 text-red-600 hover:bg-red-50"
                  aria-label={t("passkey.delete_aria")}
                >
                  <Trash2 size={12} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
